/**
 * C1b's cache (`KLQ-20260820-001` phase 3).
 *
 * **One thing polls, and it stops.** The rendition state, and only while it is `queued` or
 * `running`. Tiling takes seconds to minutes, the person is looking at a processing state, and
 * there is no other way to learn it finished — this is the poll the spec sanctions and the
 * only one. It never starts on a terminal state, and it stops at one.
 *
 * `GET /files/{id}/rendition` carries an ETag, so a poll with nothing to report costs a `304`.
 *
 * **Everything else is derived or fetched once.** The "new version available" banner comes out
 * of the version list the Versions tab already holds, refetched on window focus — no
 * subscription, no invalidation on a broad event (`~/.claude/CLAUDE.md`'s data-fetching rule,
 * and the spec's Approach §6).
 */

import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import { useInvalidateConversation } from '@/features/comments';
import { versionsQueryOptions } from '@/features/drawings';
import { PROJECTS_QUERY_KEY } from '@/features/projects';

import {
  createMarkup,
  createPin,
  deleteMarkup,
  listMarkups,
  listPins,
  readRendition,
  readVersion,
  requestRendition,
  type Markup,
  type MarkupCreate,
  type PinBody,
  type Rendition,
} from './api';
import { isMarkupKind } from './markup';
import { recordDrawn, recordRestored } from './markupSession';

function viewerKey(projectId: string) {
  return [...PROJECTS_QUERY_KEY, 'viewer', projectId] as const;
}

/**
 * How long between polls, given how many have already run.
 *
 * Bounded backoff: brisk while a fast render is plausible, then slow, then a steady half
 * minute. A pyramid that takes minutes should not be checked on every second of them, and one
 * that takes three seconds should not be found forty seconds late.
 */
export function pollDelayMs(attempt: number): number {
  const ladder = [1_000, 1_000, 2_000, 3_000, 5_000, 8_000, 13_000, 21_000];
  return ladder[attempt] ?? 30_000;
}

/** The states a poll may run in. Everything else is terminal and stops it. */
export function isPending(state: Rendition['state']): boolean {
  return state === 'queued' || state === 'running';
}

export function renditionQueryOptions(projectId: string, blobRefId: string | null) {
  return queryOptions({
    queryKey: [...viewerKey(projectId), 'rendition', blobRefId] as const,
    queryFn: () => readRendition(blobRefId ?? ''),
    enabled: blobRefId !== null,
    // The pyramid is immutable once ready: a changed drawing is a new rendition id, not an
    // edit. So a `ready` answer never goes stale and the only reason to ask again is a state
    // that has not settled — which `refetchInterval` below handles, not this.
    staleTime: Infinity,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      if (state === undefined || !isPending(state)) return false;
      return pollDelayMs(query.state.dataUpdateCount);
    },
    // A tab left open on a processing drawing should not keep asking. It catches up on focus.
    refetchIntervalInBackground: false,
  });
}

export function versionQueryOptions(projectId: string, versionId: string | null) {
  return queryOptions({
    queryKey: [...viewerKey(projectId), 'version', versionId] as const,
    queryFn: () => readVersion(versionId ?? ''),
    enabled: versionId !== null,
    staleTime: 60_000,
  });
}

/**
 * The drawing's pins, keyed on the **slot** rather than on the version.
 *
 * That is not a cache optimisation, it is the carry-forward rule expressed as a key: the
 * question "which pins are on this drawing" has one answer whichever version is on screen, so
 * paging between r01 and r04 must not refetch and must not be able to produce two different
 * lists. `staleTime` matches the conversation's 30s — the pins and the comments about them go
 * stale at the same rate because they are the same conversation.
 *
 * No polling and no subscription. A pin somebody else placed arrives on the next refetch, like
 * their comment does.
 */
export function pinsQueryOptions(projectId: string, slotId: string) {
  return queryOptions({
    queryKey: [...viewerKey(projectId), 'pins', slotId] as const,
    queryFn: () => listPins(projectId, slotId),
    staleTime: 30_000,
  });
}

export function usePins(projectId: string, slotId: string | null) {
  return useQuery({
    ...pinsQueryOptions(projectId, slotId ?? 'none'),
    enabled: slotId !== null,
  });
}

/**
 * The slot's history — the Versions tab, and the source of the superseded banner.
 *
 * **C1a's query, not a second one.** `versionsQueryOptions` owns that key; opening the viewer
 * from the tree therefore costs nothing, because the list is already cached, and a publish
 * invalidates both screens with one call. A key of our own would double the traffic to say the
 * same thing twice — and would let the two screens disagree about a drawing's history.
 *
 * The one thing added here is focus refetching. It is off globally (`app/providers.tsx`) and
 * this is the screen with a reason to want it: "somebody published while you were reading" is
 * exactly the fact the superseded banner exists to report, and a window regaining focus is the
 * cheapest honest moment to notice. It stays a derivation of an already-fetched list — no
 * subscription, no polling loop.
 */
export function useHistory(projectId: string, slotId: string | null) {
  return useQuery({
    ...versionsQueryOptions(projectId, slotId ?? 'none'),
    enabled: slotId !== null,
    refetchOnWindowFocus: true,
  });
}

/**
 * Drop a pin and its first comment, in one request.
 *
 * **Two caches change, and only one of them is this feature's.** The pin belongs to the sheet
 * and the comment belongs to the conversation, so the invalidation is the pin list *and*
 * `features/comments`' own — asked for through the hook that feature exports rather than by
 * reconstructing its key here, which would be a second place to edit the day the key changes.
 *
 * No optimistic marker. A pin the server has not accepted is a number on the sheet that may
 * turn out not to exist, and the two surfaces that share the numbering (`markers` and the
 * rail's cards) would disagree for the length of the round trip — the one thing the single
 * numbering was built to make impossible. The write is fast and the composer says it is
 * working.
 */
export function useCreatePin(projectId: string, slotId: string | null) {
  const client = useQueryClient();
  const invalidateConversation = useInvalidateConversation(projectId);
  return useMutation({
    mutationFn: (pin: Omit<PinBody, 'slot_id'>) =>
      createPin(projectId, { ...pin, slot_id: slotId ?? '' }),
    retry: false,
    onSuccess: async () => {
      // Named, so the thread refetch is exact and the feeds are only marked (`features/comments`).
      invalidateConversation(slotId);
      await client.invalidateQueries({
        queryKey: [...viewerKey(projectId), 'pins', slotId ?? 'none'] as const,
      });
    },
  });
}

/**
 * Ask for a pyramid that does not exist, or failed.
 *
 * Invalidates its own rendition query so the processing state appears immediately rather than
 * on the next poll — the person just pressed a button and deserves to see it took.
 */
export function useRequestRendition(projectId: string, blobRefId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => requestRendition(blobRefId ?? ''),
    onSuccess: async () => {
      await client.invalidateQueries({
        queryKey: [...viewerKey(projectId), 'rendition', blobRefId] as const,
      });
    },
  });
}

/**
 * The version's markup, keyed on the **version** rather than on the slot.
 *
 * That is not a cache optimisation, it is the non-carry-forward rule expressed as a key: the
 * question "which strokes are on this version" has one answer per version, so switching from
 * r03 to r04 must refetch and must not be able to produce a merged list. `staleTime` matches
 * the version's 60s — a stroke is as immutable as the sheet it is on, for the life of this
 * session.
 *
 * No polling and no subscription. A stroke somebody else drew arrives on the next refetch.
 */
export function markupsQueryOptions(projectId: string, versionId: string | null) {
  return queryOptions({
    queryKey: [...viewerKey(projectId), 'markups', versionId] as const,
    queryFn: () => listMarkups(versionId ?? ''),
    enabled: versionId !== null,
    staleTime: 60_000,
  });
}

export function useMarkups(projectId: string, versionId: string | null) {
  return useQuery({
    ...markupsQueryOptions(projectId, versionId),
    enabled: versionId !== null,
  });
}

/**
 * Draw a stroke on the open version.
 *
 * **One cache changes, and it is this feature's.** The stroke belongs to the version, so the
 * write lands in that version's markup key and nothing else. No conversation to invalidate,
 * no pin list to touch.
 *
 * `retry: false`: the endpoint takes no `Idempotency-Key` (it declares `header?: never`), so
 * a retried stroke would be a second row. The whole protection is that the mutation does not
 * retry.
 */
export function useCreateMarkup(projectId: string, versionId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<MarkupCreate, 'version_id'>) =>
      createMarkup({ ...body, version_id: versionId ?? '' }),
    retry: false,
    // The server answers with the row it wrote, so the row goes straight into the list rather
    // than refetching every stroke on the version to learn about one. Synchronous on purpose:
    // the caller drops its in-flight copy in the same tick, and a cache write that waited on a
    // refetch would leave a frame with neither drawn. An empty cache has nothing to append to
    // and is refetched instead.
    onSuccess: async (row) => {
      // Hook-level, not per `mutate`: TanStack fires a per-call callback only for the latest
      // call, and two quick strokes must both land in the session (`markupSession.ts`).
      recordDrawn(row);
      const key = markupsQueryOptions(projectId, versionId).queryKey;
      if (client.getQueryData(key) === undefined) {
        await client.invalidateQueries({ queryKey: key });
        return;
      }
      client.setQueryData(key, (page) =>
        page === undefined ? page : { ...page, items: [...page.items, row] },
      );
    },
  });
}

/**
 * Take back one stroke — the eraser, Clear, and Undo of a stroke (owner, 2026-09-17).
 *
 * The row leaves this version's cache when the server says it is gone, and a `404` says the
 * same thing: already erased from another tab, or never reachable, and either way not on the
 * sheet. `retry: false` because a `403` will not change its mind.
 */
export function useDeleteMarkup(projectId: string, versionId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (markupId: string) => {
      try {
        await deleteMarkup(markupId);
      } catch (error) {
        if (!(error instanceof ApiError && error.status === 404)) throw error;
      }
    },
    retry: false,
    onSuccess: (_nothing, markupId) => {
      const key = markupsQueryOptions(projectId, versionId).queryKey;
      client.setQueryData(key, (page) =>
        page === undefined
          ? page
          : { ...page, items: page.items.filter((row) => row.id !== markupId) },
      );
    },
  });
}

/**
 * Undo of an erase: post the strokes again, as the rows they were.
 *
 * Each one is a new row with a new id, so the session is told which old id became which
 * (`recordRestored`) and the cache gets the new rows. One refused stroke does not undo the
 * others that landed: those are recorded and cached, and the refusal is thrown for the screen
 * to show.
 */
export function useRestoreMarkups(projectId: string, versionId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (rows: readonly Markup[]) => {
      const settled = await Promise.allSettled(
        rows.map(async (row) => {
          if (!isMarkupKind(row.kind)) throw new Error(`unknown markup kind ${row.kind}`);
          const created = await createMarkup({
            version_id: row.version_id,
            kind: row.kind,
            color: row.color,
            layer: row.layer,
            geometry_json: row.geometry_json,
          });
          return [row.id, created] as const;
        }),
      );
      const pairs = settled.flatMap((one) => (one.status === 'fulfilled' ? [one.value] : []));
      recordRestored(pairs);
      const key = markupsQueryOptions(projectId, versionId).queryKey;
      if (client.getQueryData(key) === undefined) {
        await client.invalidateQueries({ queryKey: key });
      } else {
        client.setQueryData(key, (page) =>
          page === undefined
            ? page
            : { ...page, items: [...page.items, ...pairs.map(([, created]) => created)] },
        );
      }
      const refused = settled.find((one) => one.status === 'rejected');
      if (refused !== undefined) {
        throw refused.reason instanceof Error ? refused.reason : new Error(String(refused.reason));
      }
    },
    retry: false,
  });
}
